import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { apiFetch } from '../../utils/apiClient';
import StockBadge from '../../components/StockBadge';
import SyncBadge from '../../components/SyncBadge';
import { formatPrice } from '../../utils/format';

interface Vendor {
  id: number;
  name: string;
}

interface InventoryItem {
  id: number;
  book_title: string;
  book_isbn_13: string;
  book_cover_image_url: string | null;
  vendor_name: string;
  condition: string;
  selling_price: string;
  quantity: number;
  available_quantity: number;
  shopify_sync_status: string;
  is_active: boolean;
  created_at: string;
}

const AdminInventory: React.FC = () => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedVendorId, setSelectedVendorId] = useState<string>('all');
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const deleteItem = async (id: number, title: string) => {
    if (!window.confirm(`Delete "${title}" from inventory and BookMarketLk?`)) return;

    setDeletingId(id);
    try {
      const response = await apiFetch(`/api/inventory/${id}/delete-item/`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || data.message || 'Delete failed');
      }
      setItems((prev) => prev.filter((item) => item.id !== id));
    } catch (err: any) {
      alert(err.message || 'Failed to delete item');
    } finally {
      setDeletingId(null);
    }
  };

  const vendorQuery = useMemo(() => {
    if (selectedVendorId === 'all') return '';
    return `vendor=${encodeURIComponent(selectedVendorId)}`;
  }, [selectedVendorId]);

  useEffect(() => {
    void fetchVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void fetchInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorQuery]);

  const fetchVendors = async () => {
    try {
      const response = await apiFetch('/api/vendors/');
      if (!response.ok) throw new Error('Failed to fetch vendors');
      const data = await response.json();
      setVendors(data.results || []);
    } catch (err: any) {
      // Vendors list is a convenience; inventory can still load.
      console.error(err);
    }
  };

  const fetchInventory = async () => {
    setLoading(true);
    setError('');

    const path =
      vendorQuery.length > 0
        ? `/api/inventory/?${vendorQuery}`
        : '/api/inventory/';

    try {
      const response = await apiFetch(path);
      if (!response.ok) throw new Error('Failed to fetch inventory');
      const data = await response.json();
      setItems(data.results || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="w-full">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Admin Inventory</h1>
            <p className="text-sm text-gray-600 mt-1">Browse inventory across vendors</p>
          </div>
          <Link
            to="/admin"
            className="text-blue-600 active:text-blue-700 font-medium inline-flex items-center min-h-[44px] touch-manipulation"
          >
            Back to Admin
          </Link>
        </div>

        <div className="bg-white shadow sm:rounded-lg p-4 mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Vendor</label>
          <select
            className="block w-full sm:max-w-md border border-gray-300 rounded-md px-3 py-2"
            value={selectedVendorId}
            onChange={(e) => setSelectedVendorId(e.target.value)}
          >
            <option value="all">All vendors</option>
            {vendors.map((v) => (
              <option key={v.id} value={String(v.id)}>
                {v.name}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading inventory...</p>
          </div>
        ) : error ? (
          <div className="rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8 sm:p-12 text-center">
            <p className="text-gray-600">No inventory items found.</p>
          </div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {items.map((item) => (
                <li key={item.id}>
                  <div className="px-3 sm:px-4 py-4 sm:py-4">
                    <div className="flex items-start gap-3">
                      {/* Book Cover Thumbnail */}
                      <Link to={`/inventory/${item.id}`} className="flex-shrink-0 w-12 h-16 sm:w-14 sm:h-20 bg-gray-100 rounded overflow-hidden">
                        {item.book_cover_image_url ? (
                          <img
                            src={item.book_cover_image_url}
                            alt={item.book_title}
                            className="w-full h-full object-cover"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <svg className="w-6 h-6 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                          </div>
                        )}
                      </Link>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                          <Link to={`/inventory/${item.id}`} className="text-sm sm:text-base font-medium text-blue-600 truncate active:underline">
                            {item.book_title}
                          </Link>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium self-start ${
                              item.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {item.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="mt-2 space-y-1 sm:space-y-0">
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs sm:text-sm text-gray-500">
                            <p className="font-medium text-gray-800">{formatPrice(item.selling_price)}</p>
                            <p className="truncate">ISBN: {item.book_isbn_13}</p>
                            <p className="truncate">Vendor: {item.vendor_name}</p>
                            <p className="truncate capitalize">Condition: {item.condition.replace('_', ' ')}</p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 mt-1.5">
                            <StockBadge available={item.available_quantity} total={item.quantity} />
                            <SyncBadge status={item.shopify_sync_status} />
                          </div>
                        </div>
                      </div>
                      {/* Delete Button */}
                      <button
                        onClick={() => deleteItem(item.id, item.book_title)}
                        disabled={deletingId === item.id}
                        className="flex-shrink-0 p-2 text-red-400 active:text-red-600 active:bg-red-50 rounded-md min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation disabled:opacity-50"
                        title="Delete from inventory & BookMarketLk"
                      >
                        {deletingId === item.id ? (
                          <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminInventory;

